import React, { Component } from 'react';
import graphql from 'babel-plugin-relay/macro';
import { QueryRenderer } from 'react-relay';
import { Redirect } from 'react-router-dom';
import withStyles from '@mui/styles/withStyles';
import PageTitle from '../ui/PageTitle';
import {
  Button, Grid, MenuItem, Paper, Select, TextField, Typography, FormControl, InputLabel,
  Checkbox, FormControlLabel, FormGroup, IconButton, TableRow, TableCell, Table, TableBody
} from '@mui/material';
import _ from 'lodash';
import { DragPreviewImage, useDrag, useDrop } from 'react-dnd';

import AbEnvironment from '../auth/AbEnvironment';
import LoadingSpinner from '../ui/LoadingSpinner';
import ServerError from '../ui/ServerError';

import { disableAttributeCheck, getValueForRender } from '../../utils/InputUtils';
import SaveButton from '../ui/SaveButton';

import FormUpdateMutation from './FormUpdateMutation'
import FormDetailCreateMutation from './FormDetailCreateMutation'
import FormDetailDeleteMutation from './FormDetailDeleteMutation';
import FormDetailUpdateMutation from './FormDetailUpdateMutation';
import FormDetailValuesCreateMutation from './FormDetailValuesCreateMutation'
import FormDetailValuesDeleteMutation from './FormDetailValuesDeleteMutation';
import FormDetailValuesUpdateMutation from './FormDetailValuesUpdateMutation';

import { Add, Delete, DragIndicator } from '@mui/icons-material';

const ItemTypes = {
  Field: "FormDetail",
  Value: "FormDetailValue"
};

const styleSheet = theme => ({
  root: {
    width: '100%',
  },
  rootLoggedIn: {
    flexGrow: 1,
    //padding: 20,
  },
  paper: {
    textAlign: 'left',
    marginTop: 16,
    marginBottom: 0,
    paddingTop: 16,
    paddingLeft: 20,
    paddingRight: 20,
    paddingBottom: 20,
  },
  questionPaper: {
    textAlign: 'left',
    color: theme.palette.text.secondary,
    marginTop: 0,
    marginBottom: 0,
    paddingTop: 16,
    paddingLeft: 20,
    paddingRight: 20,
    paddingBottom: 20,
  },
  input: {
    minWidth: 80,
  },
  tableCell: {
    minWidth: 80,
    padding: 6
  },
  droppingInZone: {
    backgroundColor: theme.palette.colors.mediumseagreen[500]
  },
  canDrop: {
    backgroundColor: theme.palette.colors.dodgerblue[200]
  }
});

const abEnvironment = new AbEnvironment();

const FormEditQuery = graphql`
query FormEditQuery($formId: UUID!) {
  formTByFormId(formId: $formId) {
    formName
    formTitle
    formSubtitle
    description
    vppTsByFormId {
      totalCount
      nodes {
        vppId
      }
    }
    formDetailTsByFormId (orderBy: Q_ORDER_ASC) {
      totalCount
      nodes {
        formDetailId
        qOrder
        qKey
        qName
        qType
        qDefault
        qOptional
        qWidth
        formDetailValuesTsByFormDetailId (orderBy: DISPLAY_ORDER_ASC) {
          totalCount
          values: nodes {
            formDetailValuesId
            displayOrder
            text 
          }
        }
      }
    }
  }
}
`;

function disableDrag(event) {
  event.stopPropagation();
  event.preventDefault();
}

function FormDetail(props) {
  const { onChange, deleteFormDetail, deleteFormDetailValue, addFormDetailValues,
    canMoveFormDetailValue, updateFormDetailValueOrder,
    formDetail: n, stateField, index, classes } = props;

  const [{ isDragging }, drag, preview] = useDrag(() => ({
    type: ItemTypes.Field,
    item: {
      index: index,
      formDetailId: n.formDetailId,
    },
    collect: (monitor) => ({
      isDragging: !!monitor.isDragging(),
    }),
  }),
  );

  //make it so the "default" corresponds to things for the question, not just text input.
  let theDefaultValueField;

  if (n.qType === 'select') {
    const values = n.formDetailValuesTsByFormDetailId.values;
    let validDefaultValues = [""];
    const currentDefaultValue = getValueForRender(stateField?.qDefault, n.qDefault, '');

    theDefaultValueField = (
      <FormControl>
        <InputLabel shrink={true} style={{ align: 'left', marginTop: 8, marginLeft: -12 }}>Input default</InputLabel>
        <Select
          name='qDefault'
          value={currentDefaultValue}
          onChange={(e) => onChange(e, index)}
          displayEmpty
          onDragStart={disableDrag}
          draggable
          fullWidth
        >
          <MenuItem value="">Empty default value</MenuItem>
          {values && values.length > 0 && values.map((v, valuesIndex) => {
            validDefaultValues.push(String(v.displayOrder));
            return (<MenuItem key={valuesIndex} value={String(v.displayOrder)}>{v.text}</MenuItem>);
          })}
          {!validDefaultValues.includes(currentDefaultValue) && 
            <MenuItem value={String(currentDefaultValue)}>{currentDefaultValue}</MenuItem>
          }
        </Select>
      </FormControl>
    )
  } else if (n.qType === 'checkbox') {
    theDefaultValueField = (
      <FormGroup>
        <FormControlLabel
          control={
            <Checkbox
              id={'default-' + index}
              name='qDefault'
              checked={getValueForRender(stateField?.qDefault, n.qDefault, "0") === "1"}
              value={stateField?.qDefault}
              onClick={(e) => {
                e.target.checked === true ? e.target.value = "1" : e.target.value = "0";
                onChange(e, index);
              }}
              onDragStart={disableDrag}
              draggable
            />
          }
          label={'Default checked'}
          onDragStart={disableDrag}
          draggable
        />
      </FormGroup>
    );
  } else {
    theDefaultValueField = (
      <TextField
        id={'default-' + index} label={'Input default'}
        type={'text'}
        name='qDefault'
        value={getValueForRender(stateField?.qDefault, n.qDefault, '')}
        onChange={(e) => onChange(e, index)}
        inputProps={{ maxLength: 128 }}
        onDragStart={disableDrag}
        draggable
      />
    )
  }

  return (
    <Paper className={classes.questionPaper} xs={12} ref={drag}
      style={{ width: '100%', opacity: isDragging ? 0.5 : 1, }}>
      <DragPreviewImage connect={preview} />
      <FormControl>
        <Grid container spacing={1} alignItems={'center'}>
          <Grid item style={{ paddingLeft: 0 }}><DragIndicator fontSize='large' /></Grid>
          <Grid item xs={3} className={classes.input}>
            <TextField
              id={'name-' + index} label={'Input name'}
              type={'text'}
              name='qName'
              error={stateField ? stateField.error === true : false}
              value={getValueForRender(stateField?.qName, n.qName, '')}
              onChange={(e) => onChange(e, index)}
              inputProps={{ maxLength: 128 }}
              onDragStart={disableDrag}
              draggable
            />
          </Grid>
          <Grid item xs={3} className={classes.input}>
            <FormControl>
              <InputLabel style={{ align: 'left', marginTop: 8, marginLeft: -12 }}>Input type</InputLabel>
              <Select
                fullWidth
                name='qType'
                value={getValueForRender(stateField?.qType, n.qType, '')}
                onChange={(e) => onChange(e, index)}>
                <MenuItem default>Select an input type</MenuItem>
                <MenuItem value={'text'}>Text</MenuItem>
                <MenuItem value={'password'}>Password</MenuItem>s
                <MenuItem value={'number'}>Number</MenuItem>
                <MenuItem value={'select'}>Select</MenuItem>
                <MenuItem value={'checkbox'}>Checkbox</MenuItem>
                <MenuItem value={'multiLineText'}>Multi-line text field</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={2} className={classes.input}>
            {theDefaultValueField}
          </Grid>
          <Grid item style={{ minWidth: 90 }}>
            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox
                    id={'optional-' + index}
                    name='qOptional'
                    checked={getValueForRender(stateField?.qOptional, n.qOptional, 0) === 1}
                    value={stateField ? stateField.qOptional : ''}
                    onClick={(e) => {
                      e.target.checked === true ? e.target.value = Number(1) : e.target.value = Number(0);
                      onChange(e, index);
                    }}
                    onDragStart={disableDrag}
                    draggable
                  />
                }
                label={'Optional?'}
                onDragStart={disableDrag}
                draggable
              />
            </FormGroup>
          </Grid>
          <Grid item>
            <Button
              variant='outlined' color='primary'
              startIcon={<Delete color="primary" />}
              onClick={() => deleteFormDetail(index)}>
              Delete
            </Button>
          </Grid>
          <Grid item xs={12}></Grid>
          {(n.qType === 'select') &&
            <React.Fragment>
              <Grid item xs={10} style={{ paddingLeft: 16 }}>
                <Table size="small">
                  <TableBody>
                    <FormDetailValueDropZone canMoveFormDetailValue={canMoveFormDetailValue}
                      updateFormDetailValueOrder={updateFormDetailValueOrder}
                      newIndex={0} fieldIndex={index} classes={classes} />
                    {n.formDetailValuesTsByFormDetailId
                      && n.formDetailValuesTsByFormDetailId.values.length > 0
                      && n.formDetailValuesTsByFormDetailId.values.map((v, detailValuesIndex) => {

                        let stateDetailValues = stateField?.formDetailValuesTsByFormDetailId.values[detailValuesIndex];
                        //skip over values marked for delete.
                        if (stateDetailValues && stateDetailValues.markedForDelete === true) {
                          return null;
                        }

                        return (
                          <React.Fragment key={detailValuesIndex}>
                            <FormDetailValue deleteFormDetailValue={deleteFormDetailValue}
                              fieldIndex={index} detailValuesIndex={detailValuesIndex} onChange={onChange}
                              formDetailValue={v} stateField={stateField} classes={classes} />
                            <FormDetailValueDropZone canMoveFormDetailValue={canMoveFormDetailValue}
                              updateFormDetailValueOrder={updateFormDetailValueOrder}
                              newIndex={detailValuesIndex + 1} fieldIndex={index} classes={classes} />
                          </React.Fragment>
                        )
                      })
                    }
                  </TableBody>
                </Table>
              </Grid>
              <Grid item xs={12}>
                <Button
                  variant='outlined' color='primary'
                  onClick={() => addFormDetailValues(index)}
                  draggable
                  onDragStart={disableDrag}
                ><Add /> Add option
                </Button>
              </Grid>
            </React.Fragment>
          }
        </Grid>
      </FormControl>
    </Paper>
  );
}

function FormDetailDropZone(props) {
  const { updateFieldOrder, canMoveField, newIndex, classes } = props;

  const [{ isOver, canDrop }, drop] = useDrop(
    () => ({
      accept: ItemTypes.Field,
      canDrop: (dragObject) => canMoveField(newIndex, dragObject.index),
      drop: (dragObject) => updateFieldOrder(newIndex, dragObject.index),
      collect: (monitor) => ({
        isOver: !!monitor.isOver(),
        canDrop: !!monitor.canDrop()
      }),
    }),
    [],
  );

  let classToUse = '';
  if (canDrop && !isOver) classToUse = classes.canDrop;
  if (isOver && canDrop) classToUse = classes.droppingInZone;

  return (
    <Grid item xs={12} padding={1}
      className={classToUse}
      ref={drop}>
    </Grid>
  );
}

function FormDetailValue(props) {
  const { onChange, deleteFormDetailValue,
    formDetailValue: v, stateDetailValues, detailValuesIndex, fieldIndex, classes } = props;

  const [{ isDragging }, drag, preview] = useDrag(() => ({
    type: ItemTypes.Value,
    item: {
      index: detailValuesIndex,
      fieldIndex: fieldIndex,
      formDetailValuesId: v.formDetailValuesId,
    },
    collect: (monitor) => ({
      isDragging: !!monitor.isDragging(),
    }),
  }),
  );

  return (
    <TableRow ref={preview} style={{ opacity: isDragging ? 0.5 : 1 }}>
      <TableCell ref={drag} style={{ width: 20, padding: 0 }}>
        <DragIndicator />
      </TableCell>
      <TableCell className={classes.tableCell}>
        <TextField
          id={'text-' + fieldIndex + '-' + detailValuesIndex} label={'Option text'}
          type={'text'}
          name='text'
          error={stateDetailValues ? stateDetailValues.error : false}
          value={getValueForRender(stateDetailValues?.text, v.text, '')}
          onChange={(e) => onChange(e, fieldIndex, detailValuesIndex)}
          inputProps={{ maxLength: 128 }}
          onDragStart={disableDrag}
          draggable
        />
      </TableCell>
      <TableCell align="right" style={{ width: 0, padding: 6 }}
        onDragStart={disableDrag}
        draggable>
        <IconButton
          key="close"
          aria-label="Close"
          color="inherit"
          size="small"
          onClick={() => deleteFormDetailValue(fieldIndex, detailValuesIndex)}
        >
          <Delete />
        </IconButton>
      </TableCell>
    </TableRow>
  );
}

function FormDetailValueDropZone(props) {
  const { updateFormDetailValueOrder, canMoveFormDetailValue, newIndex, fieldIndex, classes } = props;

  const [{ isOver, canDrop }, drop] = useDrop(
    () => ({
      accept: ItemTypes.Value,
      canDrop: (dragObject) => canMoveFormDetailValue(newIndex, fieldIndex, dragObject.fieldIndex, dragObject.index),
      drop: (dragObject) => updateFormDetailValueOrder(newIndex, fieldIndex, dragObject.fieldIndex, dragObject.index),
      collect: (monitor) => ({
        isOver: !!monitor.isOver(),
        canDrop: !!monitor.canDrop()
      }),
    }),
    [],
  );

  let classToUse = '';
  if (canDrop && !isOver) classToUse = classes.canDrop;
  if (isOver && canDrop) classToUse = classes.droppingInZone;

  return (
    <TableRow
      style={{ width: '100%', height: 6 }}
      className={classToUse}
      ref={drop}>
      {canDrop &&
        <TableCell colSpan={3}></TableCell>
      }
    </TableRow>
  );
}

class FormEdit extends Component {

  constructor(props) {
    super(props);

    this.state = {
      formName: null,
      formNameError: false,
      formDescription: null,
      formDescriptionError: false,
      formTitle: null,
      formTitleError: false,
      formSubtitle: null,
      //changes to each field noted by index, matching that of props (minus additions)
      fieldChanges: [],
      currentFieldsJoined: null,
      saveStatus: null,
      saveMessage: null,
      saveSnackbarOpen: false,
      saveSnackbarDuration: null,
      forceFetch: 0,
    }

    //bind functions which are used in child "FormDetail"
    this.handleChangeFormDetail = this.handleChangeFormDetail.bind(this);
    this.setFormDetailValuesForDelete = this.setFormDetailValuesForDelete.bind(this);
    this.setFormDetailForDelete = this.setFormDetailForDelete.bind(this);
    this.updateFieldOrder = this.updateFieldOrder.bind(this);
    this.updateFormDetailValueOrder = this.updateFormDetailValueOrder.bind(this);
    this.canMoveField = this.canMoveField.bind(this);
    this.canMoveFormDetailValue = this.canMoveFormDetailValue.bind(this);
  }

  addFormDetail = () => {
    let { fieldChanges } = this.state;

    const theFormDetails = this.getFormDetails();
    //"newField"; if fieldChanges doesn't contain "all the old fields", increase length to include space for old fields and add the new field
    if (fieldChanges.length < theFormDetails.length) {
      fieldChanges.length = theFormDetails.length;
    }

    let { maxFieldOrder } = this;
    maxFieldOrder++;

    fieldChanges.push({
      formDetailId: null,
      qOrder: maxFieldOrder,
      qKey: 'field-' + fieldChanges.length, //the key will eventually be deprecated...
      qName: null,
      qType: 'text',
      qWidth: 12,
      qDefault: null,
      qOptional: 0,
      formDetailValuesTsByFormDetailId: {
        totalCount: 0,
        values: []
      }
    });

    //set a new "detail" field editable; if saved, create the field with all the appropriate attributes. (this is on save button of ui)
    this.updateStateAndFieldsJoined({
      saveStatus: null,
      saveMessage: null,
      fieldChanges: fieldChanges,
    });
  }

  addFormDetailValues = (indexFormDetails) => {
    let { fieldChanges } = this.state;

    let theFieldChanges = fieldChanges[indexFormDetails];
    let theValues = null;

    const theFormDetails = this.getFormDetails();
    if (theFieldChanges != null) {
      //only need to check if there isn't 'space' for the new field in the values array..
      theValues = theFieldChanges.formDetailValuesTsByFormDetailId.values;

      if (theFormDetails[indexFormDetails] && theValues.length < theFormDetails[indexFormDetails].formDetailValuesTsByFormDetailId.totalCount) {
        //fill the remaining with empty values, then do the push...
        theValues.length = theFormDetails[indexFormDetails].formDetailValuesTsByFormDetailId.totalCount;
      }
    } else {
      //establish the field as an object on fieldChanges AND the values array with empty spaces
      let theFormDetailFromDB = theFormDetails[indexFormDetails];
      theFieldChanges = {
        formDetailId: theFormDetailFromDB.formDetailId,
        formDetailValuesTsByFormDetailId: {
          totalCount: theFormDetailFromDB.formDetailValuesTsByFormDetailId.totalCount,
          values: Array(theFormDetailFromDB.formDetailValuesTsByFormDetailId.values.length)
        }
      };
      theValues = theFieldChanges.formDetailValuesTsByFormDetailId.values;
    }
    theValues.push({
      formDetailValuesId: null,
      text: '',
      displayOrder: theValues.length
    });
    theFieldChanges.formDetailValuesTsByFormDetailId.values = theValues;
    fieldChanges[indexFormDetails] = theFieldChanges;

    this.updateStateAndFieldsJoined({
      saveStatus: null,
      saveMessage: null,
      fieldChanges: fieldChanges
    })
  }

  setFormDetailForDelete(index) {
    let { fieldChanges } = this.state;
    const theFormDetails = this.getFormDetails();

    //new method: write on object with ID that the field is designated for delete...
    if (fieldChanges[index] != null) {
      fieldChanges[index]['markedForDelete'] = true;
    } else {
      //establish as an object on fieldChanges for recording the change; note the ID
      fieldChanges[index] = {
        formDetailId: theFormDetails[index].formDetailId,
        markedForDelete: true
      };
    }

    //get the field values to delete, if any...
    let theFieldFromDb = theFormDetails[index];
    if (theFieldFromDb != null) {
      let theValues = theFormDetails[index].formDetailValuesTsByFormDetailId.values;
      if (theValues && theValues.length > 0) {
        //delete all the values if they exist on the object; no need to specify if no ID.
        let deletingValuesArray = [];
        for (let i = 0; i < theValues.length; i++) {
          let theValue = theValues[i];
          deletingValuesArray[i] = {
            formDetailValuesId: theValue.formDetailValuesId,
            markedForDelete: true
          }
        }

        fieldChanges[index].formDetailValuesTsByFormDetailId = {
          totalCount: deletingValuesArray.length,
          values: deletingValuesArray
        }
      }
    }

    this.updateStateAndFieldsJoined({
      saveStatus: null,
      saveMessage: null,
      fieldChanges: fieldChanges
    });
  }

  setFormDetailValuesForDelete(indexFormDetails, indexFormDetailValues) {
    const theField = this.getFormDetails()[indexFormDetails];
    let { fieldChanges } = this.state;

    if (fieldChanges[indexFormDetails] == null) {
      //nothing currently in field changes, need to specify object
      fieldChanges[indexFormDetails] = {
        formDetailId: theField.formDetailId,
        formDetailValuesTsByFormDetailId: {
          totalCount: theField.formDetailValuesTsByFormDetailId.totalCount,
          values: Array(theField.formDetailValuesTsByFormDetailId.totalCount)
        }
      };
    }

    //we know the field exists with proper structure, check to add object or mark existing one.
    if (fieldChanges[indexFormDetails].formDetailValuesTsByFormDetailId.values[indexFormDetailValues] != null) {
      fieldChanges[indexFormDetails].formDetailValuesTsByFormDetailId.values[indexFormDetailValues].markedForDelete = true;
    } else {
      const formDetailValuesId = theField.formDetailValuesTsByFormDetailId.values[indexFormDetailValues]?.formDetailValuesId;
      fieldChanges[indexFormDetails].formDetailValuesTsByFormDetailId.values[indexFormDetailValues] = {
        markedForDelete: true,
        formDetailValuesId: formDetailValuesId
      }
    }

    //put in field changes then update the joined state object.
    this.updateStateAndFieldsJoined({
      saveStatus: null,
      fieldChanges: fieldChanges
    });
  }

  handleChange = name => event => {
    if (!event) return;
    if (!name) return;

    let valueError = name + 'Error';

    this.setState({
      saveStatus: null,
      [name]: event.target.value,
      [valueError]: false
    });
  };

  canMoveField(newIndex, droppingFieldIndex) {
    if (newIndex === droppingFieldIndex) return false;
    if (newIndex === droppingFieldIndex + 1) return false;

    return true;
  }

  updateFieldOrder(newIndex, droppingFieldIndex) {
    //don't move if can't move...
    if (!this.canMoveField(newIndex, droppingFieldIndex)) {
      return;
    }

    const theFormDetails = this.getFormDetails();
    let { fieldChanges } = this.state;

    let theFieldChange = fieldChanges[droppingFieldIndex];
    if (theFieldChange == null) {
      const theFieldFromDb = theFormDetails[droppingFieldIndex];

      //'create the field' object in changes for detailing move.
      if (theFieldFromDb != null) {
        theFieldChange = {
          formDetailId: theFieldFromDb.formDetailId,
          formDetailValuesTsByFormDetailId: {
            totalCount: theFieldFromDb.formDetailValuesTsByFormDetailId.totalCount,
            values: Array(theFieldFromDb.formDetailValuesTsByFormDetailId.totalCount),
          }
        };
      } else {
        //fieldChange should be nothing? shouldn't be able to move nothing...
      }
    }


    //need to "update" into currentFields for the sake of reordering it.
    this.updateCurrentFieldsJoined();
    let { currentFieldsJoined } = this.state;
    if (fieldChanges.length < this.getFormDetails().length) {
      fieldChanges.length = this.getFormDetails().length;
    }

    //splice out the index and re-order things.
    // JD - oldFieldChange isn't referenced as a variable, but we still need the slice operation.
    let oldFieldChange = fieldChanges.splice(droppingFieldIndex, 1); //this is just "theFieldChange" now in an array
    let oldJoinedField = currentFieldsJoined.splice(droppingFieldIndex, 1);
    let insertIndex;
    if (newIndex > droppingFieldIndex) {
      insertIndex = newIndex - 1;
    } else {
      insertIndex = newIndex;
    }
    currentFieldsJoined.splice(insertIndex, 0, ...oldJoinedField);
    fieldChanges.splice(insertIndex, 0, theFieldChange);

    //reordered, just change all the qOrder; use the current values of "fieldChanges" and "currentFieldsJoined" for the data
    for (let i = 0; i < fieldChanges.length; i++) {
      let theField = fieldChanges[i];
      if (theField == null) {
        //'create the field' object in changes (and thus currentFieldsJoined)
        const currentField = currentFieldsJoined[i];

        if (currentField != null) {
          theField = {
            formDetailId: currentField.formDetailId,
            formDetailValuesTsByFormDetailId: {
              totalCount: currentField.formDetailValuesTsByFormDetailId.totalCount,
              values: Array(currentField.formDetailValuesTsByFormDetailId.totalCount),
            }
          };
        } else {
          // throw 'issue with the field change: ', theField, i;
          let theError = 'issue with the field change: ' + theField + ' ' + i;
          throw theError;
        }
      }
      theField.qOrder = i;
      fieldChanges[i] = theField;
    };

    this.updateStateAndFieldsJoined({
      fieldChanges: fieldChanges,
      currentFieldsJoined: currentFieldsJoined,
      saveStatus: null
    });

    return theFieldChange;
  }

  canMoveFormDetailValue(newIndex, fieldIndex, droppingFieldIndex, droppingValueIndex) {
    if (fieldIndex !== droppingFieldIndex) return false;
    if (newIndex === droppingValueIndex) return false;
    if (newIndex === droppingValueIndex + 1) return false;

    return true;
  }

  updateFormDetailValueOrder(newIndex, fieldIndex, droppingFieldIndex, droppingValueIndex) {

    //don't move if can't move...
    if (!this.canMoveFormDetailValue(newIndex, fieldIndex, droppingFieldIndex, droppingValueIndex)) {
      return;
    }

    let { fieldChanges } = this.state;
    const theFormDetails = this.getFormDetails();
    const theFieldFromDb = theFormDetails[droppingFieldIndex];
    let theFieldChange = fieldChanges[droppingFieldIndex];

    //need to make sure the field change is there and the values array is established.
    if (theFieldChange == null) {

      //'create the field' object in changes for detailing move.
      if (theFieldFromDb != null) {
        theFieldChange = {
          formDetailId: theFieldFromDb.formDetailId,
          formDetailValuesTsByFormDetailId: {
            totalCount: theFieldFromDb.formDetailValuesTsByFormDetailId.totalCount,
            values: Array(theFieldFromDb.formDetailValuesTsByFormDetailId.totalCount),
          }
        };
      } else {
        //fieldChange should be nothing? shouldn't be able to move nothing...
        theFieldChange = {
          formDetailId: null,
          formDetailValuesTsByFormDetailId: {
            totalCount: 0,
            values: Array(0),
          }
        };
      }
    }

    //dealt with null field, what about null value?
    let theValue = theFieldChange.formDetailValuesTsByFormDetailId.values[droppingValueIndex];
    if (theValue == null) {
      const theValueFromDb = theFieldFromDb?.formDetailValuesTsByFormDetailId.values[droppingValueIndex];
      if (theValueFromDb != null) {
        theValue = {
          formDetailValuesId: theValueFromDb.formDetailValuesId,
        };
      }
    }

    //edit the value order, then push it into the field & fieldChanges.
    
    // JD 2022-07-17: If moving down, the drop results in landing one zone beyond where
    // it should.  To remediate, adjust the new index value if moving down.
    //
    let theNewIndex = newIndex
    if (theNewIndex > droppingFieldIndex) {
      theNewIndex = theNewIndex - 1;
    }

    theValue.displayOrder = theNewIndex;
    theFieldChange.formDetailValuesTsByFormDetailId.values[droppingValueIndex] = theValue;
    fieldChanges[droppingFieldIndex] = theFieldChange;

    //need to "update" into currentFields for the sake of reordering it.
    this.updateCurrentFieldsJoined();

    //will only be reordering values. Check for the field value to move, splice then index all.
    let { currentFieldsJoined } = this.state;
    const theValuesFromJoined = currentFieldsJoined[droppingFieldIndex]?.formDetailValuesTsByFormDetailId.values;
    let theValues = theFieldChange.formDetailValuesTsByFormDetailId.values;
    if (theValues.length < theValuesFromJoined.length) {
      theValues.length = theValuesFromJoined.length;
    }

    //we have the appropriate "buckets" for values, now re-order them.
    // JD - theValueChangeRemoved isn't referenced as a variable, but we still need the slice operation.
    let theValueChangeRemoved = theValues.splice(droppingValueIndex, 1);
    let theValueJoined = theValuesFromJoined.splice(droppingValueIndex, 1);
    theValues.splice(theNewIndex, 0, theValue);
    theValuesFromJoined.splice(theNewIndex, 0, ...theValueJoined);

    //if we want to maintain order, write all 'displayOrder' for each values...
    for (let i = 0; i < theValues.length; i++) {
      let currentValue = theFieldChange.formDetailValuesTsByFormDetailId.values[i];
      if (currentValue == null) {
        const currentValueJoined = theValuesFromJoined[i];

        if (currentValueJoined != null) {
          currentValue = {
            formDetailValuesId: currentValueJoined?.formDetailValuesId,
            displayOrder: i
          };
        } else {
          // throw 'issue with the value change: ', currentValue, i;
          let theError = 'issue with the value change: ' + currentValue + ' ' + i;
          throw theError;
        }
      }
      currentValue.displayOrder = i;
      theValues[i] = currentValue;
    }

    fieldChanges[droppingFieldIndex] = theFieldChange

    this.updateStateAndFieldsJoined({
      fieldChanges: fieldChanges,
      currentFieldsJoined: currentFieldsJoined,
      saveStatus: null
    });

    return theFieldChange;
  }

  handleChangeFormDetail(e, indexFormDetails, indexFormDetailValues) {
    let name = e.target.name;
    let value = e.target.value;

    //change typing if the values aren't appropriate
    if (name === 'qOptional' || name === 'qWidth') {
      value = Number(value);
    } else if (name === 'qOrder' || name === 'displayOrder') {
      //if blank, number is mid-change. Let it be blank.
      if (value !== '') {
        value = Number(value);
      }
    }

    let { fieldChanges, currentFieldsJoined } = this.state;
    let theFieldChange = fieldChanges[indexFormDetails];

    const theFormDetails = this.getFormDetails();

    if (theFieldChange == null) {
      //theFieldChange is null; establish as an object on fieldChanges for recording the change; note the ID
      theFieldChange = {
        formDetailId: null,
        formDetailValuesTsByFormDetailId: {
          totalCount: 0,
          values: []
        }
      };
      
      const theFieldFromDb = theFormDetails[indexFormDetails];
      if (theFieldFromDb != null) {
        //is from DB, pull all the relevant values out to populate the field change (ID and values count)
        let valuesCount = theFieldFromDb.formDetailValuesTsByFormDetailId.totalCount;
        theFieldChange.formDetailId = theFieldFromDb.formDetailId;
        theFieldChange.formDetailValuesTsByFormDetailId = {
          totalCount: valuesCount,
          values: Array(valuesCount)
        };
      }
    }

    //now we guaranteed have a fieldChange object
    if (indexFormDetailValues != null && indexFormDetailValues > -1) {
      let theValues = theFieldChange.formDetailValuesTsByFormDetailId.values;
      
      const theFieldFromDb = theFormDetails[indexFormDetails];
      //if is from DB, adjust the length of values appropriately...
      if (theFieldFromDb != null && theValues.length < theFieldFromDb.formDetailValuesTsByFormDetailId.values.length) {
        theValues.length = theFieldFromDb.formDetailValuesTsByFormDetailId.values.length;
      }
      //if the value is null, need to create it as object; if from db, make sure the ID is there.
      if (theValues[indexFormDetailValues] == null) {
        let theValueFromDb = theFieldFromDb?.formDetailValuesTsByFormDetailId.values[indexFormDetailValues];

        theValues[indexFormDetailValues] = {
          formDetailValuesId: theValueFromDb?.formDetailValuesId
        };
      }

      theValues[indexFormDetailValues][name] = value;
      //if the error flag is there, remove it (field changes and the joined ones)
      if (theValues[indexFormDetailValues].error) {
        delete theValues[indexFormDetailValues].error;
        delete currentFieldsJoined[indexFormDetails].formDetailValuesTsByFormDetailId.values[indexFormDetailValues].error;
      }
      
      fieldChanges['formDetailValuesTsByFormDetailId'] = {
        totalCount: theValues.length,
        values: theValues
      }
    } else {
      theFieldChange[name] = value;
      
      //if the error is there, remove it (field changes and the joined one)
      if (theFieldChange.error) {
        delete theFieldChange.error;
        delete currentFieldsJoined[indexFormDetails].error;
      }
    }

    //put the change to the array then update state 
    fieldChanges[indexFormDetails] = theFieldChange;

    this.updateStateAndFieldsJoined({
      saveStatus: null,
      saveMessage: null,
      fieldChanges: fieldChanges,
      currentFieldsJoined: currentFieldsJoined
    });
  }

  disableFormSave = (formProps) => {
    if (this.state.saveStatus != null) {
      return true;
    }

    const { formName, formDescription, formTitle, formSubtitle, currentFieldsJoined } = this.state;
    const { formName: propsName, formDescription: propsDescription, formTitle: propsTitle, formSubtitle: propsSubtitle } = formProps;

    //if any of the required are empty string, disable...
    if (formName === '' || formDescription === '' || formTitle === '')
      return true;

    //if the values are unchanged, flag for disable (weigh with other checks)
    let formRetVal = disableAttributeCheck(formName, propsName)
      && disableAttributeCheck(formDescription, propsDescription)
      && disableAttributeCheck(formTitle, propsTitle)
      && disableAttributeCheck(formSubtitle, propsSubtitle, true);

    //1.) nothing has changed from db changes, 2.) form required attributes are empty strings
    return formRetVal && (currentFieldsJoined == null || _.isEqual(currentFieldsJoined, this.queryFormDetails));
  }

  /**
   * Convenience method for updating both state variables and updating the "currentFieldsJoined" state after edits.
   * @param {Object} stateObjectToChange Object with properties pertaining to "what state variables to change"
   */
  updateStateAndFieldsJoined(stateObjectToChange) {
    this.setState(stateObjectToChange);

    //if changes, clear the save status stuff..
    this.setState({
      saveMessage: null,
      saveStatus: null,
      saveSnackbarOpen: null,
      saveSnackbarDuration: null
    });

    this.updateCurrentFieldsJoined();
  }

  updateCurrentFieldsJoined() {
    //there are changes made; return an array with a mix of
    let { currentFieldsJoined, fieldChanges } = this.state
    if (!currentFieldsJoined) {
      //overwrite per each attribute deep
      currentFieldsJoined = [];
      currentFieldsJoined.length = fieldChanges.length;
      currentFieldsJoined = this.copyMapIntoOtherMap(this.queryFormDetails, currentFieldsJoined, false);
    }

    currentFieldsJoined = this.copyMapIntoOtherMap(fieldChanges, currentFieldsJoined, true);

    this.setState({
      currentFieldsJoined: currentFieldsJoined
    });
  }

  copyMapIntoOtherMap(sourceMap, destMap, replace) {
    Object.keys(sourceMap).forEach(key => {
      if (sourceMap[key] != null && sourceMap[key] !== undefined) {
        //if object, need to ensure that we're not replacing the object; do deep copy:
        if (typeof sourceMap[key] === 'object') {
          //if destMap is non-existent, need to define it before scaling deep.
          if (destMap[key] == null) {
            destMap[key] = Array.isArray(sourceMap[key]) ? [] : {};
          }
          destMap[key] = this.copyMapIntoOtherMap(sourceMap[key], destMap[key], replace);
        } else {
          destMap[key] = sourceMap[key];
        }
      }
    })

    return destMap;
  }

  getFormDetails() {
    const { fieldChanges } = this.state;

    if (fieldChanges != null && fieldChanges.length > 0) {
      return this.getFormDetailsWithStateChanges();
    } else if (this.queryFormDetails != null) {
      return this.queryFormDetails;
    }
    return null;
  }

  getFormDetailsWithStateChanges() {
    const { fieldChanges, currentFieldsJoined } = this.state;
    if (fieldChanges == null || fieldChanges.length === 0) {
      return this.queryFormDetails;
    } else {
      if (currentFieldsJoined == null)
        this.updateCurrentFieldsJoined();
      return currentFieldsJoined;
    }
  }

  render() {
    const classes = this.props.classes;
    const { isAuthenticated, needAssent, isAuthenticatedVendor, logError } = this.props.auth;

    if (!isAuthenticated() || needAssent()) {
      return (<Redirect to='/home' />);
    } else if (isAuthenticatedVendor()) {

      let formId;
      if (this.props.formId) {
        formId = this.props.formId
      } else {
        const params = new URLSearchParams(this.props.location.search);
        formId = params.get('formId');
      }

      //using query renderer within the return segment...
      if (!formId) {
        return (<Redirect to='/home' />);
      }

      //account ID is the vendor
      let accountId = abEnvironment.getUserAttribute("a_id");

      return (
        <div classes={classes.rootLoggedIn}>
          <QueryRenderer
            environment={abEnvironment.getEnvironment()}
            query={FormEditQuery}
            variables={{
              formId: formId,
              accountId: accountId,
              forceFetch: this.state.forceFetch
            }}
            render={({ error, props, retry }) => {
              if (error) {
                logError(JSON.stringify(error));
                return (
                  <>
                    {/* Using this "retry" as an example; probably won't work */}
                    {!!retry &&
                      <Button onClick={() => {
                        retry();
                      }}>Reload form</Button>
                    }
                    <ServerError />
                  </>
                );
              } else if (props) {
                // if nothing is returned, then there's no access right OR the form doesn't exist
                if (props.formTByFormId === null) {
                  return (
                    <PageTitle title={"Form not found"} showBackButton={true} history={this.props.history}/>
                  );
                }

                let form = props.formTByFormId;

                //form details use state if any changes, use props from queryrenderer if none.
                this.queryFormDetails = form.formDetailTsByFormId.nodes;
                let formDetails = this.getFormDetails();

                //for tracking the highest value of "qOrder" in the list of fields.
                this.maxFieldOrder = 1;

                return (
                  <>
                    <Grid container spacing={2}>
                      <Grid container item justifyContent={'space-between'}>
                        <PageTitle title="Form editor" subtitle="Change the questions and presentation of the form" showBackButton={true} name="Program settings" noMargin={true} history={this.props.history}/>
                      </Grid>
                    </Grid>
                    <Grid container spacing={1}>
                      <Grid item xs={12}>
                        <Paper className={classes.paper} xs={12}>
                          <Grid container spacing={2}>

                            <Grid item xs={12}>
                              <Grid container spacing={1}>
                                <Grid item xs={12}>
                                  <TextField
                                    id='form-name'
                                    label='Form name'
                                    name='formName'
                                    value={getValueForRender(this.state.formName, form.formName, '')}
                                    onChange={this.handleChange('formName')}
                                    onBlur={() => {
                                      if (this.state.formName === '') {
                                        this.setState({
                                          formNameError: true
                                        })
                                      }
                                    }}
                                    helperText={this.state.formNameError ? 'cannot be empty' : ''}
                                    error={this.state.formNameError}
                                    inputProps={{ maxLength: 64 }} />
                                </Grid>
                                <Grid item xs={12}>
                                  <TextField
                                    id='form-description'
                                    size='small'
                                    multiline
                                    label='Form description'
                                    name='description'
                                    value={getValueForRender(this.state.formDescription, form.description, '')}
                                    onChange={this.handleChange('formDescription')}
                                    onBlur={() => {
                                      if (this.state.formDescription === '') {
                                        this.setState({
                                          formDescriptionError: true
                                        })
                                      }
                                    }}
                                    helperText={this.state.formDescriptionError ? 'cannot be empty' : ''}
                                    error={this.state.formDescriptionError}
                                    inputProps={{ maxLength: 1024 }} />
                                </Grid>
                                <Grid item xs={12}>
                                  <TextField
                                    id='form-title'
                                    label='Form title'
                                    name='formTitle'
                                    value={getValueForRender(this.state.formTitle, form.formTitle, '')}
                                    onChange={this.handleChange('formTitle')}
                                    onBlur={() => {
                                      if (this.state.formTitle === '') {
                                        this.setState({
                                          formTitleError: true
                                        })
                                      }
                                    }}
                                    helperText={this.state.formTitleError ? 'cannot be empty' : ''}
                                    error={this.state.formTitleError}
                                    inputProps={{ maxLength: 64 }} />
                                </Grid>
                                <Grid item xs={12}>
                                  <TextField
                                    id='form-subtitle'
                                    label='Form subtitle'
                                    size='small'
                                    multiline
                                    name='formSubtitle'
                                    value={getValueForRender(this.state.formSubtitle, form.formSubtitle, '')}
                                    onChange={this.handleChange('formSubtitle')}
                                    inputProps={{ maxLength: 1024 }} />
                                </Grid>
                              </Grid>
                            </Grid>
                            <Grid item xs={12}>
                              <Grid container spacing={2}>
                                <Grid item xs={12}>
                                  <Typography variant='h6'>Form questions</Typography>
                                </Grid>
                                <Grid item xs={12}>
                                  <Grid container spacing={1}>
                                    {formDetails.length === 0 &&
                                      <Grid item xs={12}>
                                        <Typography
                                          variant='h5'>No questions on this form. Add some with the + below.</Typography>
                                      </Grid>
                                    }
                                    <FormDetailDropZone updateFieldOrder={this.updateFieldOrder} canMoveField={this.canMoveField} newIndex={0} classes={classes} />
                                    {formDetails.map((n, index) => {
                                      let stateField = this.state.currentFieldsJoined ? this.state.currentFieldsJoined[index] : null;
                                      if (stateField && stateField.markedForDelete === true) {
                                        return null;
                                      }

                                      if (this.maxFieldOrder < n.qOrder) {
                                        this.maxFieldOrder = n.qOrder;
                                      }

                                      return (
                                        <React.Fragment key={index}>
                                          <FormDetail index={index}
                                            onChange={this.handleChangeFormDetail}
                                            deleteFormDetail={this.setFormDetailForDelete}
                                            deleteFormDetailValue={this.setFormDetailValuesForDelete}
                                            addFormDetailValues={this.addFormDetailValues}
                                            canMoveFormDetailValue={this.canMoveFormDetailValue}
                                            updateFormDetailValueOrder={this.updateFormDetailValueOrder}
                                            formDetail={n} stateField={stateField} classes={classes} />
                                          <FormDetailDropZone updateFieldOrder={this.updateFieldOrder} canMoveField={this.canMoveField} newIndex={index + 1} classes={classes} />
                                        </React.Fragment>
                                      );
                                    })}

                                    <Grid item xs={12}>
                                      <Button
                                        color="primary" variant="contained"
                                        onClick={() => this.addFormDetail()}>
                                        <Add />
                                      </Button>
                                    </Grid>
                                  </Grid>
                                </Grid>
                              </Grid>
                            </Grid>
                          </Grid>
                        </Paper>
                      </Grid>
                      <Grid item xs={12}>
                        <SaveButton
                          color="primary"
                          variant="contained"
                          buttonType='save'
                          label='Save form'
                          status={this.state.saveStatus}
                          message={this.state.saveMessage}
                          snackbarOpen={this.state.saveSnackbarOpen}
                          onSnackbarClose={() => this.setState({ saveSnackbarOpen: false })}
                          autoHideDuration={this.state.saveSnackbarDuration}
                          disabled={this.disableFormSave(form)}
                          onSaveClick={() => this._newSaveFormEditsDeep(formId)} />
                      </Grid>
                    </Grid>
                  </>
                )
              }
              return <LoadingSpinner />
            }}
          />

        </div>
      );
    } else {
      return (<Redirect to='/home' />);
    }
  }

  /**
   * inspect the form attributes and "fieldChanges" array to see if added values and changed required fields are as expected.
   */
  validateFormSave() {
    let { fieldChanges, formDescription, formName, formTitle } = this.state;

    if (formName === '') {
      this.setState({
        formNameError: true
      });
      return false;
    }
    if (formDescription === '') {
      this.setState({
        formDescriptionError: true
      });
      return false;
    }
    if (formTitle === '') {
      this.setState({
        formTitleError: true
      });
      return false;
    };

    for (let i = 0; i < fieldChanges.length; i++) {
      if (fieldChanges[i] == null) continue;
      if (fieldChanges[i].markedForDelete === true) continue;

      const theField = fieldChanges[i];
      //make sure name not null or empty; flag field as error:
      if ((theField.formDetailId == null && !theField.qName)
        || (theField.formDetailId != null && theField.qName !== undefined && !theField.qName)) {
        fieldChanges[i].error = true;

        this.updateStateAndFieldsJoined({
          fieldChanges: fieldChanges
        });
        return false;
      }

      if (theField.formDetailValuesTsByFormDetailId.values.length > 0) {
        //check each of the values...
        let theValues = theField.formDetailValuesTsByFormDetailId.values;
        for (let j = 0; j < theValues.length; j++) {
          if (theValues[j] == null) continue;
          if (theValues[j].markedForDelete === true) continue;

          const theValue = theValues[j];
          //make sure name not null or empty; flag field as error:
          if ((theValue.formDetailValuesId == null && !theValue.text)
            || (theValues.formDetailValuesId != null && theValue.text !== undefined && !theValue.text)) {
            fieldChanges[i].formDetailValuesTsByFormDetailId.values[j].error = true;
            this.updateStateAndFieldsJoined({
              fieldChanges: fieldChanges
            });
            return false;
          }
        }
      }
    }

    return true;
  }

  _newSaveFormEditsDeep(formId) {

    //validate before trying to save...
    if (!this.validateFormSave()) {
      this.setState({
        disableSave: true,
        saveStatus: 'error',
        saveMessage: 'Invalid values for saving the form',
        saveSnackbarOpen: true,
        saveSnackbarDuration: 3000,
      })
      return;
    }

    //change state variables for the button and message;
    this.setState({
      disableSave: true,
      saveStatus: 'saving'
    });

    //order of saves: deal with the simple form text, then fields, then the field values;
    const { formTitle, formSubtitle, formDescription, formName,
      fieldChanges, currentFieldsJoined } = this.state;

    //copy need to ensure it's not passing a reference...
    let theUpdatedFieldChanges = fieldChanges;

    let formUpdatePromise = null;
    let deleteFieldPromiseArray = [];
    let simpleDeepUpdatesPromiseArray = [];
    let createValueAfterFieldPromiseArray = [];

    //FORM
    if (formTitle || formSubtitle || formDescription || formName) {
      formUpdatePromise = FormUpdateMutation.commit(formId, formName, formDescription, formTitle, formSubtitle)
        .then((response) => {
          return response;
        }).catch((error) => {
          console.log(error);
          return error;
        });
      simpleDeepUpdatesPromiseArray.push(formUpdatePromise);
    }

    //FIELDS -> promise for "no fkey constraint" objects go to simple; 
    //          promise reference push to array for more complex (rely on ID or delete deep)
    if (fieldChanges && fieldChanges.length > 0) {
      for (let i = 0; i < fieldChanges.length; i++) {
        const theField = fieldChanges[i];
        if (theField == null)
          continue;

        const formDetailId = theField.formDetailId
        const priorFieldData = currentFieldsJoined[i];

        if (theField.markedForDelete === true) {
          //DELETE
          if (formDetailId == null) {
            //no need to delete from db if there's no ID associated.
            continue;
          }

          if (priorFieldData && priorFieldData.formDetailId !== null) {
            let currentValuesToDeletePromiseArray = [];
            for (let j = 0; j < priorFieldData.formDetailValuesTsByFormDetailId.totalCount; j++) {
              let theValue = priorFieldData.formDetailValuesTsByFormDetailId.values[j];
              let deleteValuePromise = FormDetailValuesDeleteMutation.commit(theValue.formDetailValuesId);
              currentValuesToDeletePromiseArray.push(deleteValuePromise);
            }

            //the values to delete will be processed BEFORE the formDetail is deleted
            let deletePromise = Promise.all(currentValuesToDeletePromiseArray).then(response => {
              let deleteFieldPromise = FormDetailDeleteMutation.commit(formDetailId);
              deleteFieldPromiseArray.push(deleteFieldPromise);

              return response;
            });
            simpleDeepUpdatesPromiseArray.push(deletePromise);
          } else {
            //the index doesn't match, ERROR
          }
        } else if (formDetailId != null) {
          //UPDATE
          const theValues = theField.formDetailValuesTsByFormDetailId.values;
          let updateValuesPromiseArray = [];
          if (theValues && theValues.length > 0) {
            for (let j = 0; j < theValues.length; j++) {
              let theValue = theValues[j];
              if (theValue == null) {
                continue;
              } else if (theValue.markedForDelete === true) {
                //DELETE VALUE
                let deleteValuePromise = FormDetailValuesDeleteMutation.commit(theValue.formDetailValuesId);
                updateValuesPromiseArray.push(deleteValuePromise);
              } else if (theValue.formDetailValuesId != null) {
                //UPDATE VALUE
                let updateValuesPromise = FormDetailValuesUpdateMutation.commit(theValue.formDetailValuesId, theValue.displayOrder, theValue.text);
                updateValuesPromiseArray.push(updateValuesPromise);
              } else {
                //ADD VALUE
                let valueToSave = {
                  displayOrder: theValue.displayOrder === '' ? j : theValue.displayOrder,
                  text: theValue.text
                }

                let createValuesPromise = FormDetailValuesCreateMutation.commit(formDetailId, valueToSave)
                  .then(response => {
                    if (response && response.createFormDetailValuesT) {
                      let addedFormDetailValuesId = response.createFormDetailValuesT.formDetailValuesT.formDetailValuesId;
                      let valueWithId = theValue;
                      theValue.formDetailValuesId = addedFormDetailValuesId;
                      theUpdatedFieldChanges[i][j] = valueWithId;
                    }
                    return response;
                  }).catch(error => {
                    // console.log('unable to create field: name=' + theField.qName);
                    // reject(error);
                  });
                updateValuesPromiseArray.push(createValuesPromise);
              }
            }
          }

          //make it a little nicer by having all the value promises for the field in one box.
          simpleDeepUpdatesPromiseArray.push(Promise.all(updateValuesPromiseArray));

          //need to remove attributes from field in update for the patch to work...
          let theCleanedField = { ...theField };
          delete theCleanedField['formDetailValuesTsByFormDetailId'];
          delete theCleanedField['formDetailId'];

          //if the only fields on this are "values" and "ID", no need to update the field itself..
          if (Object.keys(theCleanedField).length === 0)
            continue;

          //order is kinda weird... if blank, then just use "current index"
          if (theCleanedField.qOrder === '') {
            theCleanedField.qOrder = i;
          }
          let updateFieldPromise = FormDetailUpdateMutation.commit(formDetailId, theCleanedField);
          simpleDeepUpdatesPromiseArray.push(updateFieldPromise);
        } else {
          //ADD FIELD
          let { qKey, qName, qDefault, qOptional, qOrder, qType, qWidth } = theField;
          let theCleanedField = {
            qKey: qKey,
            qName: qName,
            qDefault: qDefault,
            qOptional: qOptional,
            qOrder: qOrder,
            qType: qType,
            qWidth: qWidth,
          }

          let addFieldPromise = FormDetailCreateMutation.commit(formId, theCleanedField)
            .then(response => {
              // when the field is added and created, update ID and check if values need to add
              if (response && response.createFormDetailT) {
                let addedFormDetailId = response.createFormDetailT.formDetailT.formDetailId;
                let fieldWithId = theField;
                fieldWithId.formDetailId = addedFormDetailId;
                theUpdatedFieldChanges[i] = fieldWithId;

                //are there any values entries within this new field? if so, create them ...
                if (theField.formDetailValuesTsByFormDetailId.values.length > 0) {
                  let valuesToAdd = theField.formDetailValuesTsByFormDetailId.values;
                  for (let j = 0; j < valuesToAdd.length; j++) {
                    let valueToAdd = {
                      displayOrder: valuesToAdd[j].displayOrder,
                      text: valuesToAdd[j].text
                    };

                    let createValuesPromise = FormDetailValuesCreateMutation.commit(addedFormDetailId, valueToAdd)
                      .then(response => {
                        if (response && response.createFormDetailValuesT) {
                          let addedFormDetailValuesId = response.createFormDetailValuesT.formDetailValuesT.formDetailValuesId;
                          let valueWithId = valuesToAdd[j];
                          valueWithId.formDetailValuesId = addedFormDetailValuesId;
                          theUpdatedFieldChanges[i][j] = valueWithId;
                        }
                        return response;
                      }).catch(error => {
                        // console.log('unable to create value: option=' + valueToAdd.text);
                        // reject(error);
                      });
                    createValueAfterFieldPromiseArray.push(createValuesPromise);
                  }
                }
              } else {
                //the field wasn't created if reaches here; how to handle..
              }

              return response;
            }).catch(error => {
              // console.log('unable to create field: name=' + qName);
              // reject(error);
            });

          simpleDeepUpdatesPromiseArray.push(addFieldPromise);
        }
      } //endFor of all formDetails
    }

    //After all simple promises, wait for all deep promises, then process "save completed".
    return Promise.all(simpleDeepUpdatesPromiseArray)
      .then(simpleResponse => {
        // console.log(simpleResponse)
        let secondaryDeepUpdatesPromiseArray = [...deleteFieldPromiseArray, ...createValueAfterFieldPromiseArray];

        return Promise.all(secondaryDeepUpdatesPromiseArray)
          .then(response => {
            //ONLY THEN should the page handle state for checking if the save succeeded.
            // console.log(response);
            let refetch = this.state.forceFetch;
            refetch++;

            this.setState({
              disableSave: true,
              saveStatus: 'success',
              saveMessage: 'Form saved successfully',
              saveSnackbarOpen: true,
              saveSnackbarDuration: 3000,

              //OPTION1: force fetch of data after all the saves happen; reset "changed" state values to original...
              forceFetch: refetch,
              fieldChanges: [],
              currentFieldsJoined: null,

              //OPTION2: update things in state as the save is performed such that the current data is processed.
              // fieldChanges: theUpdatedFieldChanges,
            });
          }).catch(error => {
            // console.log(error);
            this.setState({
              saveStatus: 'error',
              saveMessage: 'Form save was only partially successful',
              saveSnackbarOpen: true,
              saveSnackbarDuration: 3000
            })
          });
      }).catch(error => {
        // console.log(error);
        this.setState({
          saveStatus: 'error',
          saveMessage: 'Form save was only partially successful',
          saveSnackbarOpen: true,
          saveSnackbarDuration: 3000
        })
      });
  }
}

export default withStyles(styleSheet)(FormEdit);